import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Icon, Swatch } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { useThread } from "@/hooks/useMessages";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import type { SwatchKind } from "@/components/primitives";

interface ThreadMeta {
  scan_id: string | null;
  scan_title: string;
  scan_photo: string | null;
  scan_img: string;
  other_handle: string;
  other_display_name: string | null;
  other_avatar: SwatchKind;
  other_id: string;
}

function timeStamp(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export default function MessageThread() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { messages, loading, sending, send } = useThread(id ?? null);
  const [draft, setDraft] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Seed the composer once from ?draft=… (used by "Make offer" CTA), then strip it.
  useEffect(() => {
    const seed = searchParams.get("draft");
    if (seed) {
      setDraft(seed);
      const next = new URLSearchParams(searchParams);
      next.delete("draft");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [meta, setMeta] = useState<ThreadMeta | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Load conversation + scan + other-profile metadata
  useEffect(() => {
    if (!id || !user) return;
    let cancelled = false;
    (async () => {
      const { data: convo } = await supabase
        .from("conversations")
        .select("scan_id,buyer_id,seller_id")
        .eq("id", id)
        .maybeSingle();
      if (cancelled || !convo) return;
      const otherId = convo.buyer_id === user.id ? convo.seller_id : convo.buyer_id;
      // For general DMs, scan_id is null — skip the scan fetch (querying
      // .eq("id", null) silently returns nothing but the request is wasted).
      const [{ data: profile }, scanResult] = await Promise.all([
        (supabase as any).from("profiles_public").select("id,handle,display_name,avatar").eq("id", otherId).maybeSingle(),
        convo.scan_id
          ? supabase.from("scans").select("id,title,photos,img").eq("id", convo.scan_id).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      const scan = scanResult.data;
      if (cancelled) return;
      setMeta({
        scan_id: convo.scan_id,
        scan_title: scan?.title ?? "Listing",
        scan_photo: (scan?.photos && scan.photos.length > 0) ? scan.photos[0] : null,
        scan_img: scan?.img ?? "olive",
        other_id: otherId,
        other_handle: profile?.handle ?? "user",
        other_display_name: profile?.display_name ?? null,
        other_avatar: ((profile?.avatar ?? "cream") as SwatchKind),
      });
    })();
    return () => { cancelled = true; };
  }, [id, user]);

  // Keep latest in view
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const grouped = useMemo(() => {
    // Insert lightweight day separators
    const out: Array<{ kind: "day"; label: string } | { kind: "msg"; m: typeof messages[number] }> = [];
    let lastDay = "";
    for (const m of messages) {
      const day = new Date(m.created_at).toDateString();
      if (day !== lastDay) {
        out.push({ kind: "day", label: new Date(m.created_at).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }) });
        lastDay = day;
      }
      out.push({ kind: "msg", m });
    }
    return out;
  }, [messages]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim() || sending) return;
    const ok = await send(draft);
    if (ok) {
      setDraft("");
    }
  };

  return (
    <MobileFrame>
      <div
        className="paper-bg"
        style={{
          // The composer is pinned to the bottom of the phone screen via
          // position: fixed below (the previous flex-100dvh layout had
          // browser quirks pushing the composer off-viewport on web).
          // Inner padding-bottom reserves space so messages don't slide
          // under the fixed composer when the thread is short.
          minHeight: "calc(100dvh - env(safe-area-inset-top, 0px))",
          display: "flex", flexDirection: "column",
          minWidth: 0,
        }}
      >
        {/* Top bar with listing context */}
        <div style={{ padding: "12px 16px 10px", borderBottom: "1px solid hsl(var(--ink))", background: "hsl(var(--paper))" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={() => nav("/messages")}
              aria-label="Back to inbox"
              style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}
            >
              {Icon.chev(18, "l")}
            </button>
            {meta && (
              <button
                onClick={() => meta && nav(`/profile/@${meta.other_handle}`)}
                style={{
                  background: "none", border: "none", padding: 0, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0, textAlign: "left",
                }}
              >
                <Swatch kind={meta.other_avatar} style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid hsl(var(--ink))", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 15, color: "hsl(var(--ink))", lineHeight: 1.05, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {meta.other_display_name || meta.other_handle}
                  </div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: "hsl(var(--ink-2))", letterSpacing: "0.06em" }}>
                    @{meta.other_handle}
                  </div>
                </div>
              </button>
            )}
          </div>

          {meta && meta.scan_id && (
            <button
              onClick={() => nav(`/feed?scan=${meta.scan_id}`)}
              style={{
                marginTop: 10, width: "100%", display: "flex", gap: 8, alignItems: "center",
                background: "hsl(var(--cream))", border: "1px solid hsl(var(--ink))",
                padding: "8px 10px", cursor: "pointer", textAlign: "left",
              }}
            >
              {meta.scan_photo ? (
                <img src={meta.scan_photo} alt={meta.scan_title} style={{ width: 36, height: 36, objectFit: "cover", border: "1px solid hsl(var(--ink))", flexShrink: 0 }} />
              ) : (
                <Swatch kind={meta.scan_img as never} style={{ width: 36, height: 36, border: "1px solid hsl(var(--ink))", flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="caps" style={{ color: "hsl(var(--rust))", fontSize: 9 }}>RE: this scan</div>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 13, color: "hsl(var(--ink))", lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2 }}>
                  {meta.scan_title}
                </div>
              </div>
              <span style={{ color: "hsl(var(--ink-2))" }}>{Icon.chev(14, "r")}</span>
            </button>
          )}
        </div>

        {/* Messages — natural document flow now that the composer is
            fixed-positioned below. paddingBottom reserves vertical space
            equal to the composer's height (60px) + iOS home-indicator
            inset so the last message never sits under the composer. */}
        <div
          ref={scrollRef}
          style={{
            flex: 1, minHeight: 0,
            padding: "14px 16px calc(60px + env(safe-area-inset-bottom, 0px) + 14px)",
            display: "flex", flexDirection: "column", gap: 8,
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {loading && (
            <div className="caps" style={{ color: "hsl(var(--ink-2))", textAlign: "center", padding: "20px 0" }}>
              Loading…
            </div>
          )}
          {!loading && messages.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0", fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))" }}>
              Send the first message.
            </div>
          )}
          {grouped.map((row, i) => {
            if (row.kind === "day") {
              return (
                <div key={`day-${i}`} className="caps" style={{ textAlign: "center", color: "hsl(var(--ink-2))", fontSize: 9, padding: "6px 0" }}>
                  · {row.label} ·
                </div>
              );
            }
            const m = row.m;
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} style={{
                display: "flex", justifyContent: mine ? "flex-end" : "flex-start",
              }}>
                <div style={{
                  maxWidth: "78%",
                  background: mine ? "hsl(var(--ink))" : "hsl(var(--cream))",
                  color: mine ? "hsl(var(--cream))" : "hsl(var(--ink))",
                  border: mine ? "none" : "1px solid hsl(var(--ink))",
                  padding: "8px 12px",
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14, lineHeight: 1.35,
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                }}>
                  {m.body}
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, opacity: 0.85,
                    marginTop: 4, letterSpacing: "0.06em", textAlign: mine ? "right" : "left",
                  }}>
                    {timeStamp(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })}

        </div>

        {/* Composer — pinned to the bottom of the phone screen via
            position: fixed. Width matches MobileFrame's 430px max-width
            chrome via the same centering trick (left:50% + translateX).
            Reserves env(safe-area-inset-bottom) padding so the textarea
            sits above the iOS home indicator. */}
        <form
          onSubmit={submit}
          aria-label="Send message"
          style={{
            position: "fixed",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "100%",
            maxWidth: 430,
            zIndex: 10,
            display: "flex", alignItems: "flex-end", gap: 8,
            padding: "10px 14px calc(env(safe-area-inset-bottom, 0px) + 10px)",
            background: "hsl(var(--paper))",
            borderTop: "1px solid hsl(var(--ink))",
            boxSizing: "border-box",
          }}
        >
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Message…"
            aria-label="Message"
            rows={1}
            maxLength={2000}
            style={{
              flex: 1,
              resize: "none",
              border: "1px solid hsl(var(--ink))",
              background: "hsl(var(--cream))",
              padding: "10px 12px",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 13,
              color: "hsl(var(--ink))",
              outline: "none",
              minHeight: 40,
              maxHeight: 140,
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void submit(e as unknown as React.FormEvent);
              }
            }}
          />
          <button
            type="submit"
            disabled={sending || !draft.trim()}
            aria-label="Send"
            style={{
              flexShrink: 0,
              padding: "10px 16px",
              background: "hsl(var(--ink))",
              color: "hsl(var(--ink-fg))",
              border: "1px solid hsl(var(--ink))",
              cursor: (sending || !draft.trim()) ? "not-allowed" : "pointer",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              opacity: (sending || !draft.trim()) ? 0.5 : 1,
              minHeight: 40,
            }}
          >
            {sending ? "…" : "Send"}
          </button>
        </form>
      </div>
    </MobileFrame>
  );
}